import { useState, useEffect, useMemo } from 'react';
import { Box, Checkbox, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, TextField, Paper, Divider, TableSortLabel } from '@mui/material';
import Label from '../../../../Components/Label';
import Loader from '../../../../Components/Loader';
import useStyles from '../../styles';
import { format } from '../../../../utilities/helpers';

function ExperimentCustomTable({
    experimentData,
    header,
    rowsPerPageInit,
    rowsPerPageOptions,
    sitesLoaded,
    handleRowClick,
    handleSiteIdClick,
    defaultKey = ""
}) {
    const classes = useStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageInit);
    const [searched, setSearched] = useState('');
    const [showSyncBlocked, setShowSyncBlocked] = useState(false);
    const [showUpdateBlocked, setShowUpdateBlocked] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: defaultKey, direction: 'desc' });

    const handlePageChange = (e, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = e => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleSearch = e => {
        if (e.target.value.trim() === '') {
            setSearched('');
            return;
        }
        setSearched(e.target.value);
    };

    const handleSortRequest = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const filteredData = useMemo(() => {
        if (!experimentData) return [];
        const lowercasedSearch = searched.toLowerCase();

        return experimentData.filter(experiment => {
            const searchFilter =
                !lowercasedSearch ||
                String(experiment.domain || '')
                    .toLowerCase()
                    .includes(lowercasedSearch) ||
                String(experiment.siteId || '')
                    .toLowerCase()
                    .includes(lowercasedSearch) ||
                String(experiment.name || '')
                    .toLowerCase()
                    .includes(lowercasedSearch) ||
                String(experiment.entityTag || '')
                    .toLowerCase()
                    .includes(lowercasedSearch);

            const syncBlockFilter = !showSyncBlocked || experiment.sync_block;
            const updateBlockFilter = !showUpdateBlocked || experiment.update_block;

            return searchFilter && syncBlockFilter && updateBlockFilter;
        });
    }, [experimentData, searched, showSyncBlocked, showUpdateBlocked]);

    const sortedData = useMemo(() => {
        const sortableItems = [...filteredData];
        if (sortConfig.key) {
            const sortableColumn = header.find(h => h.id === sortConfig.key);
            if (!sortableColumn || !sortableColumn.sortable) {
                return sortableItems;
            }

            const { sortType } = sortableColumn;

            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                const aIsNA = aValue === 'NA' || aValue === null || typeof aValue === 'undefined';
                const bIsNA = bValue === 'NA' || bValue === null || typeof bValue === 'undefined';

                if (aIsNA && bIsNA) return 0;
                if (aIsNA) return sortConfig.direction === 'asc' ? -1 : 1;
                if (bIsNA) return sortConfig.direction === 'asc' ? 1 : -1;

                let comparison = 0;
                switch (sortType) {
                    case 'string':
                        comparison = String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
                        break;
                    case 'date':
                        comparison = new Date(aValue) - new Date(bValue);
                        break;
                    case 'number':
                        comparison = aValue - bValue;
                        break;
                    default:
                        comparison = String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
                        break;
                }

                return sortConfig.direction === 'asc' ? comparison : -comparison;
            });
        }
        return sortableItems;
    }, [filteredData, sortConfig, header]);

    useEffect(() => {
        setPage(0);
    }, [searched, experimentData, showSyncBlocked, showUpdateBlocked, sortConfig]);

    const validatedDate = (date) => {
        const returnedDate = date === "NA" ? "N/A" : format(new Date(date), 'MMM dd, yyyy HH:mm:ss');
        return returnedDate;
    };

    return (
        <>
            <Box
                component={Paper}
                elevation={2}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 3,
                    p: 1.5,
                    mb: 1,
                    borderRadius: 2,
                }}
            >
                {/* Search Bar */}
                <TextField
                    label="Search by Domain, Site ID, Experiment Name, or Tag"
                    variant="outlined"
                    value={searched}
                    onChange={handleSearch}
                    sx={{
                        flexGrow: 1,
                        minWidth: "350px",
                    }}
                    disabled={!sitesLoaded}
                />

                <Divider orientation="vertical" flexItem />

                {/* Filters */}
                <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showSyncBlocked}
                                onChange={(e) => setShowSyncBlocked(e.target.checked)}
                                disabled={!sitesLoaded}
                            />
                        }
                        label="Sites where sync is blocked"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showUpdateBlocked}
                                onChange={(e) => setShowUpdateBlocked(e.target.checked)}
                                disabled={!sitesLoaded}
                            />
                        }
                        label="Sites where update is blocked"
                    />
                </Box>
            </Box>

            {sitesLoaded && (
                <TableContainer className={classes.customTableContainer}>
                    <Table>
                        <TableHead className={classes.customTableHead}>
                            <TableRow>
                                {header.map(col => (
                                    <TableCell
                                        key={col.id}
                                        sortDirection={sortConfig.key === col.id ? sortConfig.direction : false}
                                    >
                                        {col.sortable ? (
                                            <TableSortLabel
                                                active={sortConfig.key === col.id}
                                                direction={sortConfig.key === col.id ? sortConfig.direction : 'desc'}
                                                onClick={() => handleSortRequest(col.id)}
                                            >
                                                <b>{col.label}</b>
                                            </TableSortLabel>
                                        ) : (
                                            <b>{col.label}</b>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedData && sortedData.length
                                ? sortedData
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => (
                                        <TableRow
                                            hover
                                            onClick={() => handleRowClick(row)}
                                            key={`${row.siteId}-${row.name}-${index}`}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>
                                                <span
                                                    onClick={e => handleSiteIdClick(row.siteId, e)}
                                                    style={{
                                                        color: '#1976d2',
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    {row.siteId}
                                                </span>
                                            </TableCell>
                                            <TableCell>{row.domain}</TableCell>
                                            <TableCell>
                                                <Label variant="ghost" color="success">
                                                    {row.entityTag}
                                                </Label>
                                            </TableCell>
                                            <TableCell>{validatedDate(row.startedAt)}</TableCell>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell>{row.description}</TableCell>
                                            <TableCell>{row.sync_block ? 'Yes' : 'No'}</TableCell>
                                            <TableCell>{row.update_block ? 'Yes' : 'No'}</TableCell>
                                        </TableRow>
                                    ))
                                : null}
                        </TableBody>

                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={rowsPerPageOptions}
                                    count={sortedData.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handlePageChange}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}

            {!sitesLoaded && <Loader />}
        </>
    );
}

export default ExperimentCustomTable;